import React, {useEffect, useState} from 'react';
import {useTranslation} from "react-i18next";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {useServerError} from "../../../../hooks/useServerError";
import apiAxios from "../../../../app/api/apiAxios";
import ResultCode from "../../../../data/enums/ResultCode";
import getServerErrorResourceName from "../../../../data/apiErrors/apiErrors";
import {toast} from "react-toastify";
import FormLayout from "../../../ui/layouts/FormLayout/FormLayout";
import Button, {ButtonType} from "../../../ui/components/Button/Button";
import BuildingForm from "./BuildingForm";
import SyncFromCrmConfirmationDialog
    from "../Building/components/SyncFromCrmConfirmationDialog/SyncFromCrmConfirmationDialog";
import VideoCamerasGrid from "./components/VideoCamerasGrid/VideoCamerasGrid";
import EntrancesGrid from "./components/EntrancesGrid/EntrancesGrid";

const initialState = {
    id: 0,
    createdOn: null,
    isBlocked: false,
    enableUnet: false,
    value: null,
    name: null
}

const Building = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {id} = useParams();
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(initialState);
    const [serverError, setServerError] = useServerError(null);
    const {state} = useLocation();
    const [isSyncFromCrmDialogOpen, setIsSyncFromCrmDialogOpen] = useState(false);

    useEffect(() => {
        (async () => await load(id))();
    }, [id]);

    const load = async (id) => {
        if (id && id > 0) {
            setServerError(null);
            setPage(initialState);
            setIsLoading(true);

            const url = `${process.env.REACT_APP_BE_DOMAIN}/api/Building/getBuilding?id=${id}`;

            try {
                const response = await apiAxios.get(url);

                if (response) {
                    if (response.status === 200 && response.data) {
                        if (response.data.resultCode === ResultCode.Success) {

                            setPage(response.data);
                        } else {
                            setServerError(t(getServerErrorResourceName({
                                model: 'building',
                                resultCode: response.data.resultCode
                            })));
                        }
                    }
                }

                setIsLoading(false);
            } catch (e) {
                if (e && e.message) {
                    setServerError(`${t(getServerErrorResourceName({
                        model: 'building',
                        resultCode: ResultCode.ServerError
                    }))} Message: (${e.message})`);
                }
                setIsLoading(false);
            }
        } else {
            if (state && state.name) {
                setPage({
                    ...initialState,
                    name: state.name
                });
            }
        }
    };

    const save = async (values, actions) => {
        try {
            setServerError(null);
            setIsLoading(true);
            actions.setSubmitting(true);

            const response = await apiAxios.post(`${process.env.REACT_APP_BE_DOMAIN}/api/building/savebuilding`, {
                id: page.id,
                isCommercial:values.isCommercial,isGroup:values.isGroup, enableUnet: values.enableUnet,
                street : values.street,
                number : values.number, section: values.section, subSection : values.subSection,
                homeOwner : values.homeOwner, unetOrgId : values.unetOrgId??null
            });

            if (response) {
                if (response.status === 200 && response.data) {
                    if (response.data.resultCode === ResultCode.Success) {
                        toast.success(t("page.form.saved-label"));

                        if (response.data.description) {
                            await load(+response.data.description);
                        }
                    } else {
                        setServerError(t(getServerErrorResourceName({
                            model: 'building',
                            resultCode: response.data.resultCode,
                            description: response.data.description
                        })));
                    }
                }
            }

            setIsLoading(false);
            actions.setSubmitting(false);
        } catch (err) {
            if (err && err.message) {
                setServerError(`${t(getServerErrorResourceName({
                    model: 'building',
                    resultCode: ResultCode.ServerError
                }))} Message: (${err.message})`);
            }
            setIsLoading(false);
            actions.setSubmitting(false);
        }
    };

    const onSyncFromCrmComplete = () => {
        setIsSyncFromCrmDialogOpen(prev => !prev);

        (async () => await load(id))();
    };

    return (
        <FormLayout header={<div className='default-form-layout-header'>{t("page.form.form-label")}</div>}
                    footer={(
                        <div className='default-form-layout-footer'>
                            <Button buttonType={ButtonType.Back}
                                    onClick={() => navigate(-1)}
                                    disabled={isLoading}>
                                {t("btn.back")}
                            </Button>
                            <div>
                                    <Button buttonType={ButtonType.Default}
                                            style={{marginLeft: '5px'}}
                                            onClick={() => setIsSyncFromCrmDialogOpen(prev => !prev)}
                                            disabled={isLoading}>
                                        {t("building.btn.sync-from-crm")}
                                    </Button>
                                </div>

                            <div>


                                <Button buttonType={ButtonType.Default}
                                        form='page-form'
                                        type='submit'
                                        disabled={isLoading}>
                                    {t("btn.save")}
                                </Button>
                            </div>
                        </div>
                    )}
                    footerPosition='top'
                    isLoading={isLoading}>
            <div className='default-form-layout-container'>
                {serverError &&
                    <div className='server-error-container'>
                        <div>
                            {serverError}
                        </div>
                    </div>
                }
                <BuildingForm {...page} onSubmit={save}/>
                {page.unetOrgId &&
                <div className={'video-container'} style={{height:600}}>
                    <VideoCamerasGrid orgId={page.unetOrgId}/>
                </div>
                }
                {page.id &&
                <div className={'entrances-container'} style={{height:600}}>
                    <EntrancesGrid buildingId={page.id}/>
                </div>}
                <SyncFromCrmConfirmationDialog open={isSyncFromCrmDialogOpen}
                                               buildingId={id}
                                               actionComplete={onSyncFromCrmComplete}
                                               close={() => setIsSyncFromCrmDialogOpen(prev => !prev)}/>
            </div>
        </FormLayout>
    );
};

export default Building;
